import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  ChatLauncherController,
  type ChatLauncherNavigationRequest,
} from 'ias-ppr-mt-chat-client';
import { Subscription } from 'rxjs';
import { PwaChatReturnNavigationService } from '@core/services/pwa-chat-return-navigation.service';

export const PWA_CHAT_PROFILE_QUERY_PARAM = 'profile';
export const PWA_CHAT_CURRENT_USER_PROFILE = 'current-user';

@Injectable({ providedIn: 'root' })
export class PwaChatNavigationService {
  private readonly launcher = inject(ChatLauncherController);
  private readonly router = inject(Router);
  private readonly returnNavigation = inject(PwaChatReturnNavigationService);
  private subscription: Subscription | null = null;

  public start(): void {
    if (this.subscription) {
      return;
    }

    this.subscription = this.launcher.navigationRequests.subscribe(
      (request) => {
        void this.navigate(request);
      },
    );
  }

  private async navigate(
    request: ChatLauncherNavigationRequest,
  ): Promise<void> {
    const conversationReference = request.conversationReference?.trim();
    const commands = conversationReference
      ? ['/chat', conversationReference]
      : ['/chat'];
    const replaceCurrentChatList =
      request.kind === 'current-user' &&
      normalizePath(this.router.url) === '/chat';
    const currentPath = normalizePath(this.router.url);
    const replacingConversation =
      Boolean(conversationReference) && isChatConversationPath(currentPath);
    const preparedInternalReturnState =
      request.kind === 'conversation' && conversationReference
        ? this.returnNavigation.consumeInternalConversationReturnState()
        : undefined;
    const returnState = replacingConversation
      ? (preparedInternalReturnState ??
        this.returnNavigation.preserveReturnState())
      : preparedInternalReturnState;

    const navigated = await this.router.navigate(commands, {
      queryParams:
        request.kind === 'message'
          ? { messageId: request.messageId }
          : request.kind === 'current-user'
            ? {
                [PWA_CHAT_PROFILE_QUERY_PARAM]: PWA_CHAT_CURRENT_USER_PROFILE,
              }
            : undefined,
      ...(replaceCurrentChatList || replacingConversation
        ? { replaceUrl: true }
        : {}),
      ...(returnState ? { state: returnState } : {}),
    });

    if (request.kind === 'message' && navigated) {
      this.launcher.acknowledgeNavigation(request.navigationRequestId);
    }
  }
}

export function pwaChatNavigationInitializer(): void {
  inject(PwaChatNavigationService).start();
}

function normalizePath(url: string): string {
  const path = url.split(/[?#]/, 1)[0].replace(/\/+$/, '');
  return path || '/';
}

function isChatConversationPath(path: string): boolean {
  return path.startsWith('/chat/');
}
